/**
 * Auto-start a stop by location — the browser half.
 *
 * Michael, Nov 3 2026: "When the crew phone is inside a stop's geofence continuously for ~60 s, mark
 * that job started automatically via the app's existing start-job route … zero taps required."
 *
 * The rules are in utils/geoAutoStop (pure, tested). This hook only:
 *   · reads the owner's settings once (GET /api/settings/auto-start, cached 5 min)
 *   · watches the phone with `navigator.geolocation.watchPosition` while the page is open and in front
 *   · hands each fix to `advance` and performs whatever one action comes back
 *   · starts a stop through the SAME route the Start button uses — POST /jobs/:id/start with
 *     `start_method: 'auto_geofence'`, so the start is on the record as automatic (jobs.start_method)
 *   · on leaving, either raises a one-tap "Done?" prompt (the default) or finishes the stop
 *
 * What it cannot do, and the page says so rather than pretending otherwise: **there is no background
 * location in a browser.** On iPhone Safari (installed to the home screen included) `watchPosition`
 * stops the moment the screen locks or the app goes to the back, and iOS gives a web page no
 * background location permission at all. So the screen has to be on with this page in front. When the
 * page comes back after being hidden both dwell clocks are dropped, because "inside for 60 seconds"
 * cannot be claimed across a gap nobody watched.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { api } from '../services/api';
import {
  advance, markActed, resetTimers, runningStop, watchWords, readAutoStopSettings,
  metersTo, usableFix,
  AUTO_STOP_DEFAULTS, EMPTY_AUTO_STOP_STATE,
  AutoStopSettings, AutoStopState, Fix, GeoStop,
} from '../utils/geoAutoStop';

export type AutoStopPrompt = { jobId: number; at: number };

export type UseAutoStop = {
  settings: AutoStopSettings;
  /** Is the watcher actually running right now? */
  watching: boolean;
  fix: Fix | null;
  /** Why there is no GPS: the browser's own words, or null. */
  gpsError: string | null;
  /** One line for the page: "Here — starting in 40 s". */
  words: string;
  /** The stop whose clock is running, and how far the phone is from it. */
  onStop: { jobId: number; meters: number | null } | null;
  /** The "Done?" question raised by leaving a started stop (onLeave: 'prompt'). */
  prompt: AutoStopPrompt | null;
  dismissPrompt: () => void;
  /** Finish the stop the prompt is about. */
  finishPrompt: () => void;
  busy: boolean;
};

const QUERY_KEY = 'auto-start-settings';

/** The owner's five numbers. Shared by every caller through react-query's cache. */
export function useAutoStopSettings(): AutoStopSettings {
  const { data } = useQuery(QUERY_KEY, () => api.get('/settings/auto-start').then(r => r.data), {
    staleTime: 5 * 60_000,
    // A login that cannot read them (or no signal) keeps the shipped numbers rather than losing auto-start.
    retry: false,
  });
  return useMemo(() => (data ? readAutoStopSettings(data) : AUTO_STOP_DEFAULTS), [data]);
}

/**
 * @param stops   the day's stops, in the order shown (`order` is their place in the day)
 * @param enabled the page's own gate: today only, not a read-only login, the list is loaded
 * @param onChanged called after a start or a finish, so the page can re-read the day
 */
export function useAutoStop({ stops, enabled, onChanged }: {
  stops: GeoStop[];
  enabled: boolean;
  onChanged?: () => void;
}): UseAutoStop {
  const settings = useAutoStopSettings();
  const qc = useQueryClient();
  const [fix, setFix] = useState<Fix | null>(null);
  const [gpsError, setGpsError] = useState<string | null>(null);
  const [prompt, setPrompt] = useState<AutoStopPrompt | null>(null);
  const [busy, setBusy] = useState(false);
  // A second-by-second re-render so the countdown in `words` actually counts down.
  const [, setTick] = useState(0);

  const stateRef = useRef<AutoStopState>(EMPTY_AUTO_STOP_STATE);
  const stopsRef = useRef<GeoStop[]>(stops);
  stopsRef.current = stops;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const changedRef = useRef(onChanged);
  changedRef.current = onChanged;
  const inFlight = useRef(false);

  const watching = enabled && settings.enabled && stops.length > 0;

  const refresh = useCallback(() => {
    qc.invalidateQueries('field-stops');
    qc.invalidateQueries('route-session-active');
    qc.invalidateQueries('day-clock');
    if (changedRef.current) changedRef.current();
  }, [qc]);

  /** Start a stop the way the Start button does, logged as automatic. */
  const startJob = useCallback(async (jobId: number, at: Fix) => {
    inFlight.current = true;
    setBusy(true);
    // Claimed before the call, so a second fix arriving mid-flight cannot start it twice.
    stateRef.current = markActed(stateRef.current, 'start', jobId);
    try {
      await api.post(`/jobs/${jobId}/start`, {
        lat: at.lat, lng: at.lng, start_method: 'auto_geofence',
      });
      refresh();
    } catch (e: any) {
      // Refused (off-fence by the server's own 150 m rule, no access, already started by someone
      // else): drop the claim so a later fix can try again, and say nothing — the crew asked for
      // zero taps, not for a toast every minute.
      const already = e?.response?.data?.code === 'ALREADY_STARTED' || e?.response?.status === 409;
      if (!already) {
        stateRef.current = { ...stateRef.current, startedIds: stateRef.current.startedIds.filter(x => x !== jobId) };
      }
      stateRef.current = resetTimers(stateRef.current);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [refresh]);

  /** Finish a stop — the crew's own Finish route, so the clock, pay and the day's texts all follow. */
  const finishJob = useCallback(async (jobId: number, at: Fix | null) => {
    inFlight.current = true;
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/complete`, at ? { lat: at.lat, lng: at.lng } : {});
      setPrompt(p => (p && p.jobId === jobId ? null : p));
      refresh();
    } catch {
      // Left on the page as the prompt, for a tap. Nothing is lost.
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, [refresh]);

  /** One reading in — at most one thing happens. */
  const onFix = useCallback((next: Fix) => {
    setFix(next);
    if (inFlight.current) return;
    const { state, action } = advance(stateRef.current, {
      stops: stopsRef.current, fix: next, now: Date.now(), settings: settingsRef.current,
    });
    stateRef.current = state;
    if (!action) return;
    if (action.kind === 'start') { void startJob(action.jobId, next); return; }
    // Leaving a started stop.
    stateRef.current = markActed(stateRef.current, 'leave', action.jobId);
    if (action.mode === 'complete') void finishJob(action.jobId, next);
    else setPrompt({ jobId: action.jobId, at: Date.now() });
  }, [startJob, finishJob]);

  // ── The watcher ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (!watching) { stateRef.current = resetTimers(stateRef.current); return; }
    if (typeof navigator === 'undefined' || !('geolocation' in navigator)) {
      setGpsError('This phone or browser will not share its location.');
      return;
    }
    setGpsError(null);
    const id = navigator.geolocation.watchPosition(
      p => { setGpsError(null); onFix({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy, at: Date.now() }); },
      err => {
        setGpsError(err.code === err.PERMISSION_DENIED
          ? 'Location is turned off for this site — turn it on in the browser settings to start stops by themselves.'
          : 'Could not get a location just now.');
      },
      { enableHighAccuracy: true, maximumAge: 5_000, timeout: 20_000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [watching, onFix]);

  // Back to the front after being hidden: both clocks are dropped. A browser gives a page no location
  // while it is in the background (iOS stops it dead), so "inside the fence for 60 seconds" cannot be
  // claimed across the gap — the dwell starts again from the next reading.
  useEffect(() => {
    if (!watching) return;
    const onShow = () => { if (document.visibilityState === 'visible') stateRef.current = resetTimers(stateRef.current); };
    document.addEventListener('visibilitychange', onShow);
    return () => document.removeEventListener('visibilitychange', onShow);
  }, [watching]);

  // The countdown on screen.
  useEffect(() => {
    if (!watching) return;
    const t = setInterval(() => setTick(x => x + 1), 1000);
    return () => clearInterval(t);
  }, [watching]);

  // A stop that got finished (or skipped) elsewhere takes its prompt with it.
  useEffect(() => {
    if (!prompt) return;
    const s = stops.find(x => x.id === prompt.jobId);
    if (!s || String(s.status || '').toLowerCase() !== 'in_progress') setPrompt(null);
  }, [stops, prompt]);

  const running = runningStop(stops);
  const words = watchWords({ state: stateRef.current, fix, stops, settings, now: Date.now() });

  return {
    settings,
    watching,
    fix,
    gpsError,
    words: watching ? words : (settings.enabled ? 'Auto-start is waiting for today\'s stops' : 'Auto-start is off'),
    onStop: running ? { jobId: running.id, meters: metersTo(running, usableFix(fix, settings.radius) ? fix : null) } : null,
    prompt,
    dismissPrompt: () => setPrompt(null),
    finishPrompt: () => { if (prompt) void finishJob(prompt.jobId, fix); },
    busy,
  };
}
